/**
 * @fileoverview Integrations Settings Page
 *
 * Purpose: Lets a firm connect its own InfoTrack API key. Morgan uses the
 * firm's key for that firm's searches, so InfoTrack bills the firm directly.
 * Only the masked last-4 of the key is ever shown; the key itself is stored
 * encrypted and never returned to the client.
 * URL: /dashboard/settings/integrations
 * Protection: Requires authentication; editing requires org owner/admin.
 *
 * Dependencies:
 * - Convex: settings.infotrack.queries.getInfotrackKeyStatus,
 *           settings.infotrack.actions.saveInfotrackApiKey,
 *           settings.infotrack.actions.testInfotrackApiKey,
 *           settings.infotrack.mutations.deleteInfotrackApiKey
 */

import { createFileRoute } from "@tanstack/react-router";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAction, useMutation } from "convex/react";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { useActiveOrg } from "@/utils/org-context";
import { Button, Input, Label } from "@/ui/primitives";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/ui/composed/overlays";
import {
  CheckCircle2,
  ChevronDown,
  Cloud,
  ExternalLink,
  HelpCircle,
  KeyRound,
  Loader2,
  PlugZap,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { MicrosoftAccountRows } from "./-ui.microsoft-account-section";
import { useProviderAccountStatuses } from "./-microsoft-providers";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/integrations",
)({
  component: IntegrationsSettings,
  beforeLoad: () => ({
    title: "Integrations",
    headerTitle: "Integrations",
    headerDescription: "Connect third-party services your firm uses.",
  }),
});

function IntegrationsSettings() {
  const { orgId, activeOrg } = useActiveOrg();
  const canEdit = activeOrg?.role === "owner" || activeOrg?.role === "admin";

  if (!orgId) {
    return (
      <div className="text-sm text-muted-foreground">
        Select an organisation to manage integrations.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InfotrackCard orgId={orgId as Id<"organizations">} canEdit={canEdit} />
      <ConnectedAccountsCard />
    </div>
  );
}

/**
 * Provider account connections (OneDrive / SharePoint / Xero). The connection
 * belongs to the signed-in user (not the org) and is reused across jobs —
 * the same rows appear on each job's Integrations tab.
 */
function ConnectedAccountsCard() {
  const accountStatuses = useProviderAccountStatuses();

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="border-b p-5">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Cloud className="size-4.5 text-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium">Connected accounts</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Connect OneDrive, SharePoint and Xero to bring documents and
              financial data into jobs. Authorize once per provider — the
              connection belongs to your Morgan account and is reused across
              jobs. Folder syncs and report imports are set up on each job's
              Integrations tab.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2 p-5">
        <MicrosoftAccountRows
          statuses={accountStatuses.data}
          statusesLoading={accountStatuses.isLoading}
          onChanged={() => void accountStatuses.refetch()}
        />
      </div>
    </div>
  );
}

function InfotrackCard({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const { data: status, isLoading } = useQuery(
    convexQuery(api.settings.infotrack.queries.getInfotrackKeyStatus, {
      orgId,
    }),
  );

  const saveKey = useAction(api.settings.infotrack.actions.saveInfotrackApiKey);
  const testKey = useAction(api.settings.infotrack.actions.testInfotrackApiKey);
  const deleteKey = useMutation(
    api.settings.infotrack.mutations.deleteInfotrackApiKey,
  );

  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const refetchStatus = () =>
    queryClient.invalidateQueries({
      queryKey: convexQuery(
        api.settings.infotrack.queries.getInfotrackKeyStatus,
        { orgId },
      ).queryKey,
    });

  const handleSave = async () => {
    const trimmed = apiKey.trim();
    if (!trimmed) {
      toast.error("Enter an API key first.");
      return;
    }
    setSaving(true);
    try {
      await saveKey({ orgId, apiKey: trimmed });
      setApiKey("");
      await refetchStatus();
      toast.success("InfoTrack API key saved.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save API key.");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await testKey({ orgId });
      if (result.ok) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not test the key.");
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteKey({ orgId });
      await refetchStatus();
      toast.success("InfoTrack API key removed.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to remove API key.");
    }
  };

  const configured = !!status?.configured;

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="border-b p-5">
        <div className="flex items-start gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
            <PlugZap className="size-4.5 text-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium">InfoTrack</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Land titles, ASIC and PPSR searches. Add your firm's InfoTrack API
              key — searches run against your account and InfoTrack bills your
              firm directly.
            </p>
          </div>
          {configured ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
              <CheckCircle2 className="size-3.5" />
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">
              Not connected
            </span>
          )}
        </div>
      </div>

      <div className="space-y-4 p-5">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {configured ? (
              <div className="flex items-center gap-2 text-sm">
                <KeyRound className="size-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  Key on file ending in
                </span>
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono">
                  ••••{status?.keyLast4}
                </code>
                {status?.updatedAt ? (
                  <span className="text-xs text-muted-foreground">
                    · updated {new Date(status.updatedAt).toLocaleDateString()}
                  </span>
                ) : null}
              </div>
            ) : null}

            {canEdit ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="infotrack-api-key">
                    {configured ? "Replace API key" : "API key"}
                  </Label>
                  <Input
                    id="infotrack-api-key"
                    type="password"
                    autoComplete="off"
                    placeholder="Paste your InfoTrack API key"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    The key is stored encrypted and never shown again after
                    saving.
                  </p>
                </div>

                <InfotrackKeyHelp />

                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : null}
                    {configured ? "Replace key" : "Save key"}
                  </Button>

                  {configured ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleTest}
                        disabled={testing}
                      >
                        {testing ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : null}
                        Test connection
                      </Button>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost">Remove</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Remove InfoTrack API key?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Searches will be blocked until a new key is added.
                              Existing search history is kept.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDelete}>
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Only organisation owners and admins can manage the InfoTrack API
                key.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const INFOTRACK_PORTAL_URL = "https://search.infotrack.com.au";
const INFOTRACK_API_DOCS_URL =
  "https://search.infotrack.com.au/app/apiknowledgehub/docs/titles-api/faqs";

/* eslint-disable @eslint-react/no-missing-key -- the steps render one at a time inside KEY_STEPS.map's keyed <li>, never as a list, so they need no keys */
const KEY_STEPS = [
  <>
    Sign in to the{" "}
    <a
      href={INFOTRACK_PORTAL_URL}
      target="_blank"
      rel="noreferrer"
      className="text-primary underline underline-offset-2"
    >
      InfoTrack Web portal
    </a>
    .
  </>,
  <>
    Click the <span className="font-medium text-foreground">gear icon</span>{" "}
    (top-right) to open{" "}
    <span className="font-medium text-foreground">Settings</span>, then choose{" "}
    <span className="font-medium text-foreground">API Keys</span> from the
    left-hand menu.
  </>,
  <>
    On the <span className="font-medium text-foreground">Generate API Key</span>{" "}
    panel, enter a key name and an expiration, then click{" "}
    <span className="font-medium text-foreground">Generate</span>.
  </>,
  <>
    Copy the new key straight away — InfoTrack only shows it once — then paste
    it into the field above and save.
  </>,
];
/* eslint-enable @eslint-react/no-missing-key */

/**
 * Collapsible "Where do I find my API key?" helper. Walks practitioners
 * through generating a key in the InfoTrack Web portal (Settings → API Keys)
 * and links out to InfoTrack's own FAQ for the full guide. Collapsed by
 * default to keep the form compact.
 */
function InfotrackKeyHelp() {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border bg-muted/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted/60"
      >
        <HelpCircle className="size-3.5 text-muted-foreground" />
        Where do I find my InfoTrack API key?
        <ChevronDown
          className={`ml-auto size-3.5 text-muted-foreground transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="space-y-4 border-t px-4 py-4">
          <p className="text-xs leading-5 text-muted-foreground">
            API keys are managed from the API Keys tab in your InfoTrack account
            settings — not inside Morgan. Generate a production key there, then
            paste it above.
          </p>

          <ol className="space-y-3">
            {KEY_STEPS.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                  {i + 1}
                </span>
                <span className="text-xs leading-5 text-muted-foreground">
                  {step}
                </span>
              </li>
            ))}
          </ol>

          <p className="rounded-md bg-amber-50 px-3 py-2.5 text-[11px] leading-4 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            Can't see the API Keys tab? It's only shown once enabled — contact
            your InfoTrack sales or account manager to request access to it.
          </p>

          <a
            href={INFOTRACK_API_DOCS_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline underline-offset-2"
          >
            Read InfoTrack's API key guide
            <ExternalLink className="size-3" />
          </a>
        </div>
      ) : null}
    </div>
  );
}
